import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import qrcode from 'src/assets/qr-code-image.png'
import { showAlertError } from 'src/components/alertError'
import { ModalLoader } from 'src/components/modalLoader'
import { useQrCodeDynamicStepForm } from 'src/context/qrcodeDynamicContext'
import { useAuth } from 'src/hooks/auth'
import {
  valueStepDynamicSchema,
  type ValueStepDynamic,
} from 'src/interface/qrCodeSchema'
import { api } from 'src/services/api'
import { isAxiosError } from 'src/utils/isAxiosError'
import { currencyMask, dateMask } from 'src/utils/maskInput'
import { setAuthToken } from 'src/utils/setAuthToken'

export const SelectValueDynamic = () => {
  const { nextStep, setFormData } = useQrCodeDynamicStepForm()
  const { account } = useAuth()
  const [isSubmiting, setIsSubmiting] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ValueStepDynamic>({
    resolver: zodResolver(valueStepDynamicSchema),
  })

  const handleChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('date_expiration', dateMask(event.target.value))
  }

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('value', currencyMask(event.target.value))
  }

  const onSubmit = async (data: ValueStepDynamic) => {
    setIsSubmiting(true)
    const newDate = new Date(data.date_expiration)
    const date_expiration = newDate.toISOString()

    try {
      setAuthToken()
      const response = await api.post('/pix/generate/dynamic', {
        amount: Number(data.value.replace(',', '.')),
        date_expiration,
        counterparty: {
          name: data.name,
          tax_number: data.tax_number,
        },
        description: 'Pix Dinâmico',
        options: {
          show_qrcode_image: true,
          allow_change_the_amount_on_payment: true,
        },
        virtual_account_id: account.virtual_account_id,
      })
      setFormData({
        hash: response.data.data.hash,
        amount: data.value,
        image: response.data.data.image,
      })

      nextStep()
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        const errorMessage = (error.response.data as { code: string }).code
        showAlertError(errorMessage)
      } else {
        showAlertError('Ocorreu um erro inesperado.')
      }
    } finally {
      setIsSubmiting(false)
    }
  }

  if (isSubmiting) {
    return <ModalLoader />
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-center px-4"
    >
      <h2 className="text-[20px] font-bold">Preencha os campos abaixo</h2>
      <div className="flex gap-5 items-center">
        <div className="p-1 border-2 border-[#433F3F] rounded-[10px] flex justify-center items-center">
          <img src={qrcode} alt="qrcode" className="w-72 h-72" />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col">
            <h2 className="text-[17px] font-bold">Valor que deseja cobrar</h2>
            <input
              className="border border-[#163172] rounded-md w-[300px] text-[#404040] py-2 px-4 bg-white"
              placeholder="R$0,00"
              {...register('value')}
              onChange={handleChangeAmount}
            />
            {errors.value && (
              <p className="text-red-500 text-[15px]">{errors.value.message}</p>
            )}
          </div>
          <div className="flex flex-col">
            <h2 className="text-[17px] font-bold">Nome do pagador</h2>
            <input
              className="border border-[#163172] rounded-md w-[300px] text-[#404040] py-2 px-4 bg-white"
              placeholder="Digite um nome"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-red-500 text-[15px]">{errors.name.message}</p>
            )}
          </div>
          <div className="flex flex-col">
            <h2 className="text-[17px] font-bold">
              Data de expiração do Qr Code
            </h2>
            <input
              className="border border-[#163172] rounded-md w-[300px] text-[#404040] py-2 px-4 bg-white"
              placeholder="DD/MM/YYYY"
              {...register('date_expiration')}
              onChange={handleChangeDate}
            />
            {errors.date_expiration && (
              <p className="text-red-500 text-[15px]">
                {errors.date_expiration.message}
              </p>
            )}
          </div>
          <div className="flex flex-col">
            <h2 className="text-[17px] font-bold">Documento do pagador</h2>
            <input
              className="border border-[#163172] rounded-md w-[300px] text-[#404040] py-2 px-4 bg-white"
              placeholder="Digite seu documento"
              {...register('tax_number')}
            />
            {errors.tax_number && (
              <p className="text-red-500 text-[15px]">
                {errors.tax_number.message}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-center w-full">
        <button
          type="submit"
          className="bg-[#004A8A] rounded-md mt-3 text-[15px] w-[45%] font-bold text-white py-2 px-4"
        >
          Gerar a QR code para cobrança
        </button>
      </div>
    </form>
  )
}
